import type { CreateCommentsInput } from 'src/API'
import * as mutations from 'src/graphql/mutations'
import * as queries from 'src/graphql/queries'
import { graphqlApi } from 'src/redux/api/graphql/graphqlApi'
import type { ReduxComments } from 'src/types/apiType'
import { API, graphqlOperation } from 'src/utils/amplify/legacy'
import { getErrorMessage } from 'src/utils/error'
import { makeErrorToast, msg } from 'src/utils/makeToast'
import { getProperUpdateValues } from 'src/utils/updateApiUtil'

export const commentsApi = graphqlApi.injectEndpoints({
	endpoints: (builder) => ({
		fetchCommentsByQuoteId: builder.query({
			query: ({ quoteId }: { quoteId: string }) => ({
				queryName: 'commentsByQuoteId',
				data: {
					quoteId,
					filter: { activeFlag: { eq: true } },
					limit: 1000,
				},
				isList: true,
			}),
			transformResponse: (response: ReduxComments[]) =>
				response as ReduxComments[],
			providesTags: (result) =>
				result
					? [
							...result.map(({ id }) => ({
								type: 'Comments' as const,
								id: id as string,
							})),
							{ type: 'Comments', id: 'LIST' },
						]
					: [{ type: 'Comments', id: 'LIST' }],
		}),
		fetchCommentsById: builder.query({
			query: (id: string) => ({
				queryName: 'getComments',
				data: {
					id,
				},
				isList: false,
			}),
			transformResponse: (response: ReduxComments) => {
				const typedResponse = response
				return typedResponse
			},
			providesTags: (result, error, id) => [{ type: 'Comments', id }],
		}),
		createCommentsById: builder.mutation({
			queryFn: async (arg: CreateCommentsInput) => {
				try {
					await API.graphql(
						graphqlOperation(mutations.createComments, {
							input: arg,
						})
					)
					return { data: null }
				} catch (error) {
					makeErrorToast(msg)
					const errorMessage = getErrorMessage(error)
					console.error(
						'errorMessage createCommentsById: ',
						errorMessage
					)
					console.error('errorMessage createCommentsById arg: ', arg)
					return { error: errorMessage }
				}
			},
			invalidatesTags: () => [{ type: 'Comments', id: 'LIST' }],
		}),
		updateCommentsById: builder.mutation({
			queryFn: async ({
				id,
				arg,
			}: {
				id: string
				arg: Partial<ReduxComments>
			}) => {
				try {
					const {
						data: { getComments },
					} = (await API.graphql(
						graphqlOperation(queries.getComments, { id })
					)) as {
						data: { getComments: ReduxComments }
					}
					const updatedComments =
						getProperUpdateValues<ReduxComments>({
							updatedInput: arg,
							originalInput: getComments,
						})
					await API.graphql(
						graphqlOperation(mutations.updateComments, {
							input: updatedComments,
						})
					)
					return { data: null }
				} catch (error: any) {
					makeErrorToast(msg)
					return { error }
				}
			},
			invalidatesTags: () => [{ type: 'Comments', id: 'LIST' }],
		}),
		deleteCommentsById: builder.mutation({
			queryFn: async ({ commentId }: { commentId: string }) => {
				try {
					await API.graphql(
						graphqlOperation(mutations.deleteComments, {
							commentId,
						})
					)
					return { data: null }
				} catch (error: any) {
					makeErrorToast(msg)
					return { error }
				}
			},
			invalidatesTags: () => [{ type: 'Comments', id: 'LIST' }],
		}),
	}),
	overrideExisting: false,
})

export const {
	useFetchCommentsByQuoteIdQuery,
	useCreateCommentsByIdMutation,
	useUpdateCommentsByIdMutation,
	useDeleteCommentsByIdMutation,
} = commentsApi
